import Text from "@components/common/Text";
import Title from "@components/common/Title";
import useAuth from "@components/hooks/useAuth";
import Layout from "@components/layouts/Layout";
import axios from "axios";
import { Link } from "gatsby";
import moment from "moment-timezone";
import React, { useState } from "react";
import { AiOutlineChrome, AiOutlineLoading } from "react-icons/ai";
import {
    MdCheckBox,
    MdOutlineFavorite,
    MdOutlineFavoriteBorder,
} from "react-icons/md";
import { toast } from "react-toastify";
import useSWR from "swr";
import HeroSection from "../../components/newVersion/HeroSection";

const endpoints = {
    convocatorias: "/.netlify/functions/convocatorias",
};

const fetcher = async (url, token) => {
    try {
        const config = token
            ? { headers: { Authorization: "Bearer " + token } }
            : {};
        const response = await axios.post(url, null, config);
        return response.data;
    } catch (error) {
        console.error("Error al realizar la solicitud:", error);
        throw new Error("Error al cargar datos");
    }
};

const Component = () => {
    const { state } = useAuth();

    // Clave para useSWR
    const key = state?.jwt
        ? [endpoints.convocatorias, state.jwt]
        : [endpoints.convocatorias];

    // Obtener datos y estado de carga desde useSWR
    const { data, isLoading } = useSWR(key, fetcher);

    const [filter, setFilter] = useState("");

    // Filtramos convocatorias, con tildes y sin tildes
    let filteredData =
        filter !== ""
            ? data?.data.filter(
                  (i) =>
                      i.institucioDesenvolupat
                          .toLowerCase()
                          .normalize("NFD")
                          .replace(/\p{Diacritic}/gu, "")
                          .includes(
                              filter
                                  .normalize("NFD")
                                  .replace(/\p{Diacritic}/gu, ""),
                          ) ||
                      i.titol
                          .toLowerCase()
                          .normalize("NFD")
                          .replace(/\p{Diacritic}/gu, "")
                          .includes(
                              filter
                                  .normalize("NFD")
                                  .replace(/\p{Diacritic}/gu, ""),
                          ) ||
                      i.dataPublicacio?.toLowerCase().includes(filter) ||
                      i.dataFinalitzacio?.toLowerCase().includes(filter) ||
                      i.estat?.toLowerCase().includes(filter),
              )
            : [];

    return (
        <>
            <Layout
                seo={{
                    title: "Convocatorias de Oposiciones disponibles",
                    description: "Consulta todas las plazas de policía local.",
                    image: "/images/bgs/bgCido.jpg",
                }}
                mode="fixed"
            >
                <HeroSection
                    filename="bgs/bgCido"
                    firstTitle="Convocatorias de Oposiciones disponibles"
                    secondTitle="Consulta todas las plazas de policía local"
                    thirdTitle="Encuentra las convocatorias activas, revisa requisitos y plazos, y prepara tu siguiente paso con información clara y actualizada para optar a tu plaza. Todo lo esencial, en un solo sitio, para avanzar con **seguridad y criterio**."
                    showScrollDownIcon={true}
                />

                <div className="container py-10">
                    <Title>Convocatorias de Oposiciones disponibles</Title>
                    <Text className="text-justify">
                        A continuación te ofrecemos una lista con las
                        convocatorias de plazas convocadas y toda la información
                        al respecto.
                    </Text>

                    {isLoading && (
                        <div className="mt-5">
                            <AiOutlineLoading className="h-5 w-5 animate-spin text-center duration-300" />
                        </div>
                    )}

                    {data && (
                        <div className="mt-10">
                            {/** Filtro y número de resultados */}
                            <div className="flex items-center justify-between text-gray-600">
                                <div>
                                    Se han encontrado{" "}
                                    <strong className="font-semibold text-ccyan">
                                        {filter === ""
                                            ? data?.data?.length
                                            : filteredData.length || 0}
                                    </strong>{" "}
                                    resultado/s.
                                </div>

                                <div>
                                    <input
                                        type="text"
                                        placeholder="Filtrar por texto"
                                        className="rounded border border-gray-300 px-3 py-1 text-sm text-gray-700"
                                        onChange={(e) =>
                                            setFilter(
                                                e.target.value.toLocaleLowerCase(),
                                            )
                                        }
                                        value={filter}
                                    />
                                </div>
                            </div>

                            {/* Mostramos resultados*/}
                            <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                                {(filter === "" ? data.data : filteredData).map(
                                    (item, idx) => {
                                        return (
                                            <Item
                                                key={`${item.id}-${idx}`}
                                                data={item}
                                                idx={idx}
                                                favorite={
                                                    data?.favorites.indexOf(
                                                        item.id,
                                                    ) !== -1
                                                }
                                            />
                                        );
                                    },
                                )}
                            </div>
                        </div>
                    )}

                    {/** Disclaimer */}
                    <div className="mt-10 text-center text-xs text-gray-500 md:ml-auto md:w-1/2 md:text-right">
                        * Los resultados que se muestran en esta página están
                        extraídos de la de{" "}
                        <a
                            href="https://dadesobertes.diba.cat/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="border-b text-gray-600 duration-300 hover:border-transparent"
                        >
                            Dades Obertes de la DIBA
                        </a>
                        . iOpos no se ha responsable de la exactitud de estas.
                    </div>
                </div>
            </Layout>
        </>
    );
};

const Item = ({ data, idx, favorite: fav }) => {
    const { state, isAuthenticated } = useAuth();
    const [favorite, setFavorite] = useState(fav || false);
    const [loading, setLoading] = useState(false);

    // En caso que haya un cambio
    // respecto el valor inicial pues enviamos al server la modificación
    const handleState = async () => {
        setLoading(true);

        const id = toast.loading("Guardando...", {
            position: "bottom-center",
        });

        try {
            const url = `/.netlify/functions/convocatorias-change-favorite`;
            const token = state?.jwt;

            // Hacemos el cambio
            const json = await axios.post(
                url,
                {
                    id: data.id,
                    value: !favorite,
                },
                {
                    ...(token && {
                        headers: { Authorization: "Bearer " + token },
                    }),
                },
            );

            if (json?.data?.success) {
                setFavorite(json?.data?.value);
                toast.update(id, {
                    render: json?.data?.value
                        ? "Convocatoria guardada correctamente."
                        : "Convocatoria eliminada correctamente.",
                    type: "success",
                    isLoading: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    autoClose: 3000,
                });
            } else {
                toast.update(id, {
                    render: "La convocatoria no se ha podido procesar.",
                    type: "error",
                    isLoading: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    autoClose: 5000,
                });
            }
        } catch (e) {
            console.log(e);

            toast.update(id, {
                render: "La convocatoria no se ha podido procesar.",
                type: "error",
                isLoading: false,
                closeOnClick: true,
                pauseOnHover: true,
                autoClose: 5000,
            });
        }

        setLoading(false);
    };

    return (
        <div
            style={{ minHeight: "275px" }}
            className={`relative overflow-hidden rounded shadow-md ${
                idx % 2 === 0 ? "bg-gray-50 bg-opacity-50" : "bg-white"
            }`}
        >
            <div className="flex justify-between space-x-5 bg-ccyan-dark px-5 py-2 pr-2 text-white">
                <h1
                    title={data?.institucioDesenvolupat}
                    className="lines-2-clamp uppercase text-white"
                >
                    {data?.institucioDesenvolupat}
                </h1>
                <div
                    className="pt-1 text-xs text-gray-300"
                    style={{ fontSize: "10px" }}
                    title="Data publicació"
                >
                    {moment(data?.dataPublicacio)
                        .tz("Europe/Madrid")
                        .format("DD/MM/YYYY")}
                </div>
            </div>

            <div className="m-5 space-y-2">
                <h2
                    title={data?.titol}
                    className="truncate-3-lines text-sm font-semibold uppercase text-gray-500"
                >
                    {data?.titol}
                </h2>

                <div className="flex items-center space-x-1">
                    {data?.idEstat === 3 && (
                        <MdCheckBox className="h-4 w-4 text-green-700" />
                    )}
                    {data?.idEstat === 2 && (
                        <MdCheckBox className="h-4 w-4 text-yellow-700" />
                    )}
                    <span className="text-sm">{data?.estat}</span>
                </div>

                {data?.idEstat === 3 && (
                    <div className="flex items-center space-x-2 text-sm">
                        <span className="font-semibold text-gray-500">
                            Fecha finalización:
                        </span>
                        <span className="text-gray-800">
                            {data?.dataFinalitzacio ? (
                                moment(data?.dataFinalitzacio)
                                    .tz("Europe/Madrid")
                                    .format("DD/MM/YYYY")
                            ) : (
                                <>-</>
                            )}
                        </span>
                    </div>
                )}

                <div className="absolute bottom-0 left-0 mt-10 w-full">
                    <div className="mx-3 mb-3 flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <Link
                                to={`/convocatoria/${data?.id}`}
                                title="Más información sobre la convocatoria"
                                className="rounded bg-ccyan px-5 py-2 text-white duration-300 hover:bg-ccyan-dark"
                            >
                                <AiOutlineChrome className="h-4 w-4" />
                            </Link>
                        </div>

                        {isAuthenticated && (
                            <button
                                disabled={loading}
                                className={`duration-300 ${
                                    loading
                                        ? "animate-pulse cursor-wait opacity-50"
                                        : "opacity-100"
                                }`}
                                title="Marcar/Desmarcar como favorito"
                                onClick={() => handleState()}
                            >
                                {favorite ? (
                                    <MdOutlineFavorite className="h-6 w-6 text-red-700" />
                                ) : (
                                    <MdOutlineFavoriteBorder className="h-6 w-6 text-red-700" />
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Component;
